import { useMemo } from 'react';
import {
  booleanContains,
  intersect,
  polygon,
  featureCollection,
} from '@turf/turf';
import { Polygon } from 'geojson';
import { RegridResponse } from '../../../models/regrid.model.tsx';
import { FloodRegion } from '../../../models/floodzone.models.tsx';
import { LotSideIndexes, Position } from '../types/position.ts';
import { useThrottledValue } from '../../../hooks/use-throttled-value.ts';
import { GetWetlandsDataResType } from '../services/wetlands.ts';
import { HomePlacementErrors } from '../constants/errors.ts';
import { getSetbackCoordinates } from '../utils/coords-math.ts';

interface UseHomePlacementValidationProps {
  homeModelCornerCoordinates: Position[];
  regridData?: RegridResponse;
  wetlandsData?: GetWetlandsDataResType;
  floodZoneData?: { floodregions: FloodRegion[] };
  lotSideIndexes: LotSideIndexes;
  validVertices: Position[];
}

export const useHomePlacementValidation = (
  props: UseHomePlacementValidationProps
) => {
  const throttledProps = useThrottledValue(props, 500);

  const homeModelPlacementError = useMemo(() => {
    const {
      homeModelCornerCoordinates,
      regridData,
      wetlandsData,
      floodZoneData,
      lotSideIndexes,
      validVertices,
    } = throttledProps;

    let homePlacementError = undefined;

    if (
      homeModelCornerCoordinates[0][0] !== 0 &&
      !Number.isNaN(homeModelCornerCoordinates[0][0]) &&
      homeModelCornerCoordinates[0][1] !== 0 &&
      !Number.isNaN(homeModelCornerCoordinates[0][1]) &&
      regridData?.parcelData.geometry.coordinates[0]
    ) {
      // Ensure home model coordinates form a closed polygon
      const loopingHomeModelCoords = [...homeModelCornerCoordinates];
      if (
        loopingHomeModelCoords.length > 0 &&
        (loopingHomeModelCoords[0][0] !==
          loopingHomeModelCoords[loopingHomeModelCoords.length - 1][0] ||
          loopingHomeModelCoords[0][1] !==
            loopingHomeModelCoords[loopingHomeModelCoords.length - 1][1])
      ) {
        loopingHomeModelCoords.push(loopingHomeModelCoords[0]);
      }

      // Ensure property boundary coordinates form a closed polygon
      const propertyCoords = [
        ...(regridData?.parcelData.geometry.coordinates[0] || []),
      ];
      if (
        propertyCoords.length > 0 &&
        (propertyCoords[0][0] !==
          propertyCoords[propertyCoords.length - 1][0] ||
          propertyCoords[0][1] !== propertyCoords[propertyCoords.length - 1][1])
      ) {
        propertyCoords.push(propertyCoords[0]);
      }

      // Validate we have enough points for a polygon
      if (loopingHomeModelCoords.length < 4 || propertyCoords.length < 4) {
        console.error('Not enough points to form valid polygons');
        return homePlacementError;
      }

      const homeModelBoundary = polygon([loopingHomeModelCoords]);
      const propertyBoundary = polygon([propertyCoords]);

      const completelyWithinPropertyBoundary = booleanContains(
        propertyBoundary,
        homeModelBoundary
      );

      //within property boundary
      if (!completelyWithinPropertyBoundary) {
        homePlacementError = HomePlacementErrors.PROPERTY_BOUNDARY_ERROR;
      }
      let completelyWithinSetbacks = true;
      let touchingWetlands;
      let touchingFloodzone;

      // within setbacks
      if (completelyWithinPropertyBoundary) {
        if (
          lotSideIndexes.backSideIndex !== -1 &&
          lotSideIndexes.frontSideIndex !== -1
        ) {
          const setbackCoordinates = getSetbackCoordinates(
            validVertices || [],
            lotSideIndexes,
            regridData
          );

          // Ensure setback coordinates form a closed polygon
          if (setbackCoordinates && setbackCoordinates.length > 0) {
            const closedSetbackCoords = [...setbackCoordinates];
            if (
              closedSetbackCoords[0][0] !==
                closedSetbackCoords[closedSetbackCoords.length - 1][0] ||
              closedSetbackCoords[0][1] !==
                closedSetbackCoords[closedSetbackCoords.length - 1][1]
            ) {
              closedSetbackCoords.push(closedSetbackCoords[0]);
            }

            if (closedSetbackCoords.length >= 4) {
              const setbackPolygon = polygon([closedSetbackCoords]);
              completelyWithinSetbacks = booleanContains(
                setbackPolygon,
                homeModelBoundary
              );
            } else {
              console.warn('Not enough points in setback coordinates');
              completelyWithinSetbacks = true;
            }
          } else {
            completelyWithinSetbacks = true;
          }
        } else {
          completelyWithinSetbacks = true;
        }
      }

      if (!completelyWithinSetbacks) {
        homePlacementError = HomePlacementErrors.SETBACK_ERROR;
      }

      if (completelyWithinPropertyBoundary && completelyWithinSetbacks) {
        touchingWetlands = wetlandsData?.features
          .map((wd) => wd.geometry.coordinates[0])
          ?.some((polyCoords) => {
            if (!polyCoords || polyCoords.length < 4) {
              console.warn('Invalid wetland geometry');
              return false;
            }
            const wetlandPolygon = polygon([polyCoords]);
            return intersect(
              featureCollection([homeModelBoundary, wetlandPolygon])
            );
          });
        if (touchingWetlands) {
          homePlacementError = HomePlacementErrors.WETLAND_ERROR;
        }
      }

      if (
        completelyWithinPropertyBoundary &&
        !touchingWetlands &&
        completelyWithinSetbacks
      ) {
        const floodZoneCoors: [[number, number]][] = [];

        floodZoneData?.floodregions.forEach((region: FloodRegion) => {
          const geo = JSON.parse(region.geojson) as Polygon;
          if (geo.coordinates[0]) {
            floodZoneCoors.push(geo.coordinates[0] as [[number, number]]);
          }
        });
        touchingFloodzone = floodZoneCoors?.some(
          (polyCoords: [number, number][]) => {
            if (polyCoords && polyCoords.length >= 4) {
              try {
                const wetlandPolygon = polygon([polyCoords]);
                return intersect(
                  featureCollection([homeModelBoundary, wetlandPolygon])
                );
              } catch (e) {
                console.warn('Error processing flood zone polygon:', e);
                return false;
              }
            }
            return false;
          }
        );

        if (touchingFloodzone) {
          homePlacementError = HomePlacementErrors.FLOOD_ZONE_WARNING;
        }
      }

      if (
        !touchingFloodzone &&
        !touchingWetlands &&
        completelyWithinPropertyBoundary &&
        completelyWithinSetbacks
      ) {
        homePlacementError = undefined;
      }
    }

    return homePlacementError;
  }, [throttledProps]);

  return homeModelPlacementError;
};
